/* eslint-disable max-lines-per-function */
import {useContext, useRef, forwardRef, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import heic2any from 'heic2any';
import {toastAlert} from '~library/atoms/Toast';
import {GCS_PATH_IDS, gcsUploadImage} from '~/common/storage/gcs_storage';
import {charcoal, mexicanOpalWebText} from '~libSass/base/_exports.sass';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {
  selectActiveCardEditor,
  selectCountOfUyoImages,
  selectCurrentSection,
  selectFabricArtBackgroundImage,
  selectShouldUseCachedGalleryPhoto,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {useNavigator} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/NavBottom/hooks/useNavigator';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {AddPhotosLineIcon} from '~library/svg/AddPhotosLineIcon';
import {UploadIcon} from '~library/svg/UploadIcon';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {useImageUpload} from '~/CreateFabricInvitation/components/PhotoEditor/common/useImageUpload';
import {dataURLToFile} from '~/common/utils/dataURLToFile';
import {
  GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME,
  GALLERY_PHOTO_PREVIEW_MEDIA_ID,
} from '~/common/indexed-db/dexieStores';

import './UploadButton.sass';

export const imageFileUpload = async (file, eventId) => {
  if (file.size > 10 ** 7) {
    toastAlert('Please select an image less than 10MB.', {
      className: 'uyo_toast__error',
      title: 'This image is too large',
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} color={mexicanOpalWebText} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    throw new Error('Please select an image less than 10MB.');
  }

  const gcsUploadArgs = {
    gcsPathId: GCS_PATH_IDS.anonymous_image,
    params: {
      event_id: eventId,
      image_id: '',
    },
    image: file,
  };

  return gcsUploadImage(gcsUploadArgs);
};

let isImgRendered = false;

export const UploadButton = forwardRef(
  (
    {
      section,
      editor,
      className = '',
      styles,
      callback,
      id,
      index = 1,
      btnText = '',
      isInNavBottom = false,
      showFullWidth = false,
    },
    ref
  ) => {
    const {canvas} = useContext(EventCreateContext);
    const fileRef = useRef();
    const dispatch = useDispatch();
    const isLargishAndUp = useIsLargishAndUp();
    const isMobile = !isLargishAndUp;
    const navigate = useNavigator({isMobile, disableAutoOpenEditor: true});
    const {getCachedMediaDataById} = useCachedMedia(GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME);
    const countOfUyoImages = useSelector(selectCountOfUyoImages);
    const backgroundImage = useSelector(selectFabricArtBackgroundImage);
    const shouldUseCachedGalleryPhoto = useSelector(selectShouldUseCachedGalleryPhoto);
    const isMultipleUyo = countOfUyoImages > 1;
    const {uploading, handleImageUpload} = useImageUpload(canvas);
    const media = getCachedMediaDataById(GALLERY_PHOTO_PREVIEW_MEDIA_ID);

    const onClickHandler = useCallback(() => {
      if (isInNavBottom) {
        navigate('card_design');
      } else {
        fileRef.current.click();
      }
    }, [isInNavBottom, navigate]);

    const onChangeImage = async (event) => {
      const files = event.target ? event.target.files : [];
      let [file] = files;

      if (file) {
        try {
          // since canvas can't render heic files, convert to jpeg
          if (file.type === 'image/heic') {
            const blob = await heic2any({blob: file, toType: 'image/jpeg', quality: 0.9});
            file = new File([blob], 'image.jpeg');
          }

          await handleImageUpload(file, {
            id,
            callback,
            resetInputRef: fileRef,
            isMultipleUyo,
            backgroundImage,
          });

          dispatch({
            type: UPDATE_DESIGN_OPTIONS,
            payload: {
              activeCardEditor: 'image',
              shouldUseCachedGalleryPhoto: false,
            },
          });
        } catch (error) {
          // Error handling is done in the hook
        }
      }
    };

    const activeCardEditor = useSelector(selectActiveCardEditor);
    const currentSection = useSelector(selectCurrentSection);

    useEffect(() => {
      async function uploadCachedImgToCloud() {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {activeCardEditor: 'image'},
        });

        await handleImageUpload(dataURLToFile(media, 'anonymous_image'), {
          id,
          callback,
          resetInputRef: fileRef,
          isMultipleUyo,
          backgroundImage,
        });
      }

      if (media?.length > 0 && shouldUseCachedGalleryPhoto && !isImgRendered) {
        uploadCachedImgToCloud();
        isImgRendered = true;
      }
    }, [media, shouldUseCachedGalleryPhoto]);

    const activeClass = () => {
      if (editor) {
        return editor === activeCardEditor ? 'create_flow__nav_bottom__section--active' : '';
      }
      return currentSection === section ? 'create_flow__nav_bottom__section--active' : '';
    };

    const activeButtonClass = () => {
      if (editor) {
        return editor === activeCardEditor ? 'active' : '';
      }
      return currentSection === section ? 'active' : '';
    };

    if (isInNavBottom) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
        <div
          className={`create_flow__nav_bottom__section ${activeClass()}`}
          role="button"
          onClick={onClickHandler}
          data-qa-id="nav-bottom-photo-upload"
          ref={ref}
        >
          <input
            ref={fileRef}
            type="file"
            id="uyo-footer-upload-input"
            accept="image/png,image/jpeg"
            onChange={onChangeImage}
            hidden
          />
          <div className="create_flow__nav_bottom__section__button-wrapper">
            <Button
              id="navBottom"
              variant="secondary"
              size="small"
              className={`create_flow__nav_bottom__section__button ${activeButtonClass()}`}
              data-qa-id="add-photos-upload-button"
            >
              <AddPhotosLineIcon color={charcoal} ratio={0.7} />
            </Button>
          </div>
          <p className="create_flow__nav_bottom__section__title">Photo</p>
        </div>
      );
    }

    // Can't use ECL Button component as we (correctly) don't allow the style prop, but is needed here for Canvas positioning
    return (
      <button
        id={`uyoBtn--${index}`}
        className={`button secondary large uyo-upload-button ${btnText ? 'hasText' : ''} ${showFullWidth ? 'wide' : ''} ${className}`}
        onClick={onClickHandler}
        data-qa-id={`photo-editor_upload-button--${index}`}
        type="button"
        style={styles}
        ref={ref}
      >
        <input
          ref={fileRef}
          type="file"
          id={`uyo-upload-input--card-${index}`}
          accept="image/png,image/jpeg"
          onChange={onChangeImage}
          hidden
        />
        {uploading ? (
          <span className="evt-spinner inline no-margin" />
        ) : (
          <>
            <UploadIcon className="action_button__upload" color={charcoal} ratio={0.5} />
            {btnText && (
              <Typography
                classNames="uyo-upload-button__text"
                variant="paragraph3"
                size="medium"
                color="charcoal"
              >
                {btnText}
              </Typography>
            )}
          </>
        )}
      </button>
    );
  }
);
